import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const [settings, setSettings] = useState({
    hero_tagline: "Stay Tran-Quil on the River",
    hero_subtitle: "Escape to 1.5 acres of private riverfront paradise between Lac du Bonnet and Powerview–Pine Falls, Manitoba",
    hero_image: "https://images.unsplash.com/photo-1650513452784-c91c0436f6d5?auto=format&fit=crop&w=1920&q=80"
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setSettings(prev => ({
          ...prev,
          hero_tagline: response.data.hero_tagline || prev.hero_tagline,
          hero_subtitle: response.data.hero_subtitle || prev.hero_subtitle,
          hero_image: response.data.hero_image || prev.hero_image
        }));
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-500"
        style={{ backgroundImage: `url(${settings.hero_image})` }}
      >
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/60 via-stone-900/40 to-stone-900/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Location Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8 animate-fade-in">
          <MapPin className="w-4 h-4 text-emerald-400" />
          <span className="text-stone-200 text-sm font-medium">Manitoba, Canada</span>
        </div>

        {/* Main Tagline */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight">
          <span className="block">Stay</span>
          <span className="block text-emerald-400 italic font-serif">Tran-Quil</span>
          <span className="block text-3xl md:text-4xl lg:text-5xl font-light mt-2 text-stone-200">on the River</span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-stone-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          {settings.hero_subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            asChild
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30 hover:-translate-y-1"
          >
            <Link to="/book">Book Your Stay</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full transition-all duration-300 bg-transparent"
          >
            <a href="#gallery">View Gallery</a>
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#about" className="text-white/60 hover:text-white transition-colors">
          <ChevronDown className="w-8 h-8" />
        </a>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-900 to-transparent" />
    </section>
  );
};

export default HeroSection;
